/* eslint-disable react/no-array-index-key */
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  Typography,
  Snackbar,
  Alert,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
} from '@mui/material'
import { ClientStudentInstance } from 'common/utils/types/account-types-client'
import { Operation } from 'common/utils/types/operation'
import useCurrentTeacher from 'common/context/TeacherContext'
import SelectStudents from './steps/SelectStudents'
import SelectProgram from './steps/SelectProgram'
import { DEFAULT_PROGRAM_SETTINGS } from './constants'
import Confirmation from './steps/Confirmation'

const STEPS = [
  {
    id: 0,
    label: 'Select students',
    disabledNext: (
      selectedStudents: ClientStudentInstance[],
      selectedProgram: typeof DEFAULT_PROGRAM_SETTINGS
    ) => selectedStudents.length === 0,
  },
  {
    id: 1,
    label: 'Select program',
    disabledNext: (
      selectedStudents: ClientStudentInstance[],
      selectedProgram: typeof DEFAULT_PROGRAM_SETTINGS
    ) =>
      !Object.values(selectedProgram.operation).some(Boolean) ||
      !selectedProgram.timerThreshold ||
      !selectedProgram.difficultyLevel ||
      !selectedProgram.programLength,
  },
  {
    id: 2,
    label: 'Confirmation',
    disabledNext: () => false,
  },
]

function ProgramSettings() {
  const { currentTeacher, studentsPerClassroom, ensureStudentsAndClassroom } =
    useCurrentTeacher()
  const [selectedStudents, setSelectedStudents] = useState<
    ClientStudentInstance[]
  >([])
  const [selectedProgram, setSelectedProgram] = useState<
    typeof DEFAULT_PROGRAM_SETTINGS
  >(DEFAULT_PROGRAM_SETTINGS)
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState('')
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>(
    'success'
  )
  const [isSaving, setIsSaving] = useState(false)

  const [activeStep, setActiveStep] = useState(0)

  const handleSelectStudents = useCallback(
    (students: ClientStudentInstance[]) => {
      setSelectedStudents(students)
      setSelectedProgram(DEFAULT_PROGRAM_SETTINGS)
    },
    []
  )

  useEffect(() => {
    if (!studentsPerClassroom) return

    const parseUrlForStudent = () => {
      try {
        const parentUrl = window.parent.location.href
        if (!parentUrl.includes('program_settings')) return

        const match = parentUrl.match(/program_settings\/([^/]+)\/([^/]+)/)
        if (!match) return

        const classroomCode = match[1]
        const studentCode = match[2]

        const student = studentsPerClassroom[classroomCode]?.find(
          (s) => s.code === studentCode
        )
        if (student) {
          setSelectedStudents([student])
          setActiveStep(1)
        }
      } catch {
        /* ignore */
      }
    }

    parseUrlForStudent()
  }, [ensureStudentsAndClassroom, studentsPerClassroom])

  const convertProgramToAttributes = () => {
    const { operation, timerThreshold, difficultyLevel, programLength } =
      selectedProgram

    let mathOperationIdMix = 0
    if (operation.addition)
      mathOperationIdMix |= Operation.MATH_OPERATION_IDS.addition
    if (operation.subtraction)
      mathOperationIdMix |= Operation.MATH_OPERATION_IDS.subtraction
    if (operation.multiplication)
      mathOperationIdMix |= Operation.MATH_OPERATION_IDS.multiplication
    if (operation.division)
      mathOperationIdMix |= Operation.MATH_OPERATION_IDS.division

    const problemSetIdMap: Record<string, number> = {
      beginning: Operation.PROBLEMSET_IDS.beginning,
      regular: Operation.PROBLEMSET_IDS.regular,
      extended: Operation.PROBLEMSET_IDS.extended,
    }
    const problemSetId = difficultyLevel
      ? problemSetIdMap[difficultyLevel]
      : Operation.PROBLEMSET_IDS.regular

    const thresholdIdMap: Record<string, number> = {
      '12': Operation.THRESHOLD_IDS.twelve,
      '6': Operation.THRESHOLD_IDS.six,
      '3': Operation.THRESHOLD_IDS.three,
      '2': Operation.THRESHOLD_IDS.two,
      '1.5': Operation.THRESHOLD_IDS.onePointFive,
    }
    const thresholdId = timerThreshold
      ? thresholdIdMap[timerThreshold]
      : Operation.THRESHOLD_IDS.twelve

    const assessmentOnlyId =
      programLength === 'fluencyAssessment'
        ? Operation.ASSESSMENT_ONLY_IDS.assessmentOnly
        : 0

    return {
      mathOperationIdMix: mathOperationIdMix.toString(),
      problemSetId: problemSetId.toString(),
      thresholdId: thresholdId.toString(),
      assessmentOnlyId: assessmentOnlyId.toString(),
    }
  }

  const handleConfirm = async () => {
    if (isSaving || selectedStudents.length === 0) return

    const { operation, timerThreshold, difficultyLevel } = selectedProgram
    const hasOperation = Object.values(operation).some(Boolean)

    if (!hasOperation) {
      setSnackbarMessage('Please select at least one operation.')
      setSnackbarSeverity('error')
      setSnackbarOpen(true)
      return
    }

    if (!timerThreshold) {
      setSnackbarMessage('Please select a timer threshold.')
      setSnackbarSeverity('error')
      setSnackbarOpen(true)
      return
    }

    if (!difficultyLevel) {
      setSnackbarMessage('Please select a difficulty level.')
      setSnackbarSeverity('error')
      setSnackbarOpen(true)
      return
    }

    setIsSaving(true)
    try {
      const studentsMap = selectedStudents.reduce(
        (acc, student) => {
          acc[student.code] = student
          return acc
        },
        {} as Record<string, ClientStudentInstance>
      )

      const attrs = convertProgramToAttributes()
      await currentTeacher.changeStudentPrograms(studentsMap, attrs)

      setSnackbarMessage('Programs updated successfully.')
      setSnackbarSeverity('success')
      setSnackbarOpen(true)
      setSelectedStudents([])
      setSelectedProgram(DEFAULT_PROGRAM_SETTINGS)
      setActiveStep(0)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error saving programs:', error)
      setSnackbarMessage('Failed to update programs. Please try again.')
      setSnackbarSeverity('error')
      setSnackbarOpen(true)
    } finally {
      setIsSaving(false)
    }
  }

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (
        event.data.type === 'classroom-refreshed' ||
        event.data.type === 'kids-fetched'
      ) {
        if (event.data.type === 'classroom-refreshed') {
          ensureStudentsAndClassroom?.(
            event.data.students,
            event.data.classroom
          )
        }
        const parseUrlForStudent = () => {
          try {
            const parentUrl = window.parent.location.href
            if (!parentUrl.includes('program_settings')) {
              setSelectedStudents([])
              setSelectedProgram(DEFAULT_PROGRAM_SETTINGS)
              setActiveStep(0)
              return
            }

            const match = parentUrl.match(/program_settings\/([^/]+)\/([^/]+)/)
            if (!match) {
              setSelectedStudents([])
              setSelectedProgram(DEFAULT_PROGRAM_SETTINGS)
              setActiveStep(0)
              return
            }

            const classroomCode = match[1]
            const studentCode = match[2]

            if (studentsPerClassroom?.[classroomCode]) {
              const student = studentsPerClassroom[classroomCode].find(
                (s) => s.code === studentCode
              )
              if (student) {
                setSelectedStudents([student])
                setActiveStep(1)
              } else {
                setSelectedStudents([])
                setSelectedProgram(DEFAULT_PROGRAM_SETTINGS)
                setActiveStep(0)
              }
            } else {
              setSelectedStudents([])
              setSelectedProgram(DEFAULT_PROGRAM_SETTINGS)
              setActiveStep(0)
            }
          } catch {
            setSelectedStudents([])
            setSelectedProgram(DEFAULT_PROGRAM_SETTINGS)
            setActiveStep(0)
          }
        }

        parseUrlForStudent()
      }
    }
    window.parent.addEventListener('message', handler)

    return () => {
      window.parent.removeEventListener('message', handler)
    }
  }, [studentsPerClassroom])

  const filteredSteps = useMemo(() => {
    if (currentTeacher?.isParent && !currentTeacher?.isTeacher) {
      return STEPS.filter((step) => step.id !== 0)
    }
    return STEPS
  }, [currentTeacher?.isParent, currentTeacher?.isTeacher])

  const activeStepIndex = useMemo(() => {
    if (filteredSteps.length === 0) return 0
    const idx = filteredSteps.findIndex((step) => step.id === activeStep)
    return idx === -1 ? 0 : idx
  }, [activeStep, filteredSteps])

  return (
    <Box>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        sx={{ bottom: '15% !important', left: '30% !important' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
      <Typography variant='h5'>Program Settings</Typography>
      <Typography variant='body2'>
        Build the right program for your{' '}
        {currentTeacher?.isParent && !currentTeacher?.isTeacher
          ? `children's`
          : "student's"}{' '}
        learning goals. All their current progress will stay saved, nothing will
        be deleted.
      </Typography>
      <Box sx={{ mt: 4 }}>
        <Stepper
          activeStep={activeStepIndex}
          orientation='horizontal'
          color='secondary'
          sx={{ mb: 3, maxWidth: '700px', mx: 'auto' }}
        >
          {filteredSteps.map((step, index) => (
            <Step key={step.id}>
              <StepLabel
                sx={
                  activeStepIndex >= index
                    ? {
                        '& .MuiStepIcon-root': {
                          color: '#ffc641 !important',
                        },
                      }
                    : {}
                }
              >
                {step.label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>
      {activeStep === 0 && (
        <SelectStudents onSelectStudents={handleSelectStudents} />
      )}
      {activeStep === 1 && (
        <SelectProgram
          selectedStudents={selectedStudents}
          selectedProgram={selectedProgram}
          setSelectedProgram={setSelectedProgram}
        />
      )}
      {activeStep === 2 && (
        <Confirmation
          selectedStudents={selectedStudents}
          selectedProgram={selectedProgram}
        />
      )}
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        {((currentTeacher?.isParent && activeStep > 1) ||
          (currentTeacher?.isTeacher && activeStep > 0)) && (
          <Button
            variant='text'
            sx={{ color: 'common.black' }}
            size='large'
            onClick={() => setActiveStep(activeStep - 1)}
          >
            BACK
          </Button>
        )}
        <Button
          variant='contained'
          color='secondary'
          size='large'
          onClick={() => {
            if (activeStep === 2) {
              handleConfirm()
            } else {
              setActiveStep(activeStep + 1)
            }
          }}
          disabled={
            STEPS[activeStep].disabledNext(selectedStudents, selectedProgram) ||
            isSaving
          }
        >
          {(() => {
            if (isSaving) {
              return (
                <>
                  <CircularProgress size={16} color='inherit' sx={{ mr: 1 }} />
                  Saving...
                </>
              )
            }
            return activeStep === 2 ? 'CONFIRM' : 'NEXT'
          })()}
        </Button>
      </Box>
    </Box>
  )
}

export default ProgramSettings
